// Prefer "message" (often validation/specific) over "error" (often generic HTTP label e.g. "Bad Request")
const MESSAGE_KEYS = ['message', 'errors', 'error'] as const;
const MAX_DEPTH = 3;

/**
 * Extracts a user-facing error message from an unknown error (e.g. from catch blocks or API responses).
 * - If the error is a string or string[], returns it (arrays are joined with spaces).
 * - If the error is an object, searches for `message`, `errors`, or `error` (in that order of
 *   preference) on the object and inside every property, up to 3 levels deep (covers e.g. axios
 *   `response.data`, NestJS bodies). Prefers `message` so validation text is used over generic
 *   HTTP labels like "Bad Request".
 *
 * @param error - Unknown error (string, string[], or object with nested message fields)
 * @param defaultMessage - Fallback when no message is found (default: "An error occurred")
 * @returns A single string suitable for display to the user
 */
export function extractErrorMessage(
  error: unknown,
  defaultMessage = 'An error occurred',
): string {
  if (error == null) return defaultMessage;

  const normalized = normalizeMessage(error);
  if (normalized) return normalized;

  if (typeof error === 'object') {
    const errorData = (error as any)?.response?.data || error;
    const msg = extractFromObject(errorData, 0);
    if (msg) return msg;
  }

  if (error instanceof Error && error.message) return error.message;
  return defaultMessage;
}

function normalizeMessage(value: unknown): string | null {
  if (value == null) return null;
  if (typeof value === 'string' && value.trim() !== '') return value.trim();
  if (Array.isArray(value)) {
    const parts = value
      .filter((v): v is string => typeof v === 'string')
      .map((s) => s.trim())
      .filter(Boolean);
    return parts.length > 0 ? parts.join(' ') : null;
  }
  return null;
}

function extractFromObject(obj: object, depth: number): string | null {
  if (depth > MAX_DEPTH) return null;
  if (typeof obj !== 'object' || obj === null) return null;

  for (const key of MESSAGE_KEYS) {
    const value = (obj as Record<string, unknown>)[key];
    const msg = normalizeMessage(value);
    if (msg) return msg;
  }

  for (const key of Object.keys(obj)) {
    const value = (obj as Record<string, unknown>)[key];
    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      const msg = extractFromObject(value as object, depth + 1);
      if (msg) return msg;
    }
  }

  return null;
}
